import { useState, type ReactElement } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog } from '@marketplace/ui';
import { type SubscriptionPlans } from '@marketplace/contracts';
import { BillingButton } from './BillingButton.js';
import { PlanPrice } from './PlanPrice.js';
import { billingPath, planFor, type BillingActionData, type PlanKind } from './billing.js';

/**
 * `W5-T07` §4.4, §4.10 — **Switch to professional / manitas**, which asks first.
 *
 * The confirm names the new price and says what the switch costs, by direction: manitas → professional
 * pays the prorated difference now (`billing.switch.confirm`), professional → manitas takes it off the
 * next invoice (`billing.switch.confirm_down`). The server decides the proration; this only says it.
 *
 * Posted by a fetcher, so the answer comes back to this dialog: a `402 SUBSCRIPTION_PAYMENT_FAILED`
 * keeps it open with the error and **Update card** (nothing changed: §4.4 step 5), and a `200` closes
 * it while the route re-reads the subscription. Each opening is its own fetcher, so an earlier answer
 * is never shown again.
 */
export function SwitchPlan({
  locale,
  to,
  plans,
}: {
  locale: string;
  to: PlanKind;
  plans: SubscriptionPlans | null;
}): ReactElement {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(0);
  const [asking, setAsking] = useState(false);
  const fetcher = useFetcher<BillingActionData>({ key: `billing-switch-${to}-${opened}` });

  const answer = fetcher.state === 'idle' ? fetcher.data : undefined;
  const failure = answer !== undefined && 'failure' in answer ? answer.failure : null;
  const switched = answer !== undefined && 'switched' in answer;
  const plan = planFor(plans, to);
  const label = t(`billing.action.switch_to.${to}`);

  const open = () => {
    setOpened((count) => count + 1);
    setAsking(true);
  };
  const confirm = () => {
    void fetcher.submit(
      { intent: 'switch', kind: to },
      { method: 'post', action: billingPath(locale) },
    );
  };

  return (
    <>
      <Button variant="secondary" onPress={open}>
        {label}
      </Button>

      {!asking || switched ? null : (
        <Dialog
          title={label}
          isOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) setAsking(false);
          }}
          footer={
            <>
              <Button variant="secondary" onPress={() => setAsking(false)}>
                {t('billing.switch.cancel')}
              </Button>{' '}
              <Button onPress={confirm} isDisabled={fetcher.state !== 'idle'}>
                {label}
              </Button>
            </>
          }
        >
          {plan === null ? null : <PlanPrice plan={plan} locale={locale} testId="billing-switch" />}
          <p>{t(to === 'PRO' ? 'billing.switch.confirm' : 'billing.switch.confirm_down')}</p>
          {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}
          {failure === 'billing.error.plan_payment_failed' ? (
            <BillingButton
              locale={locale}
              intent="portal"
              label={t('billing.action.update_card')}
              variant="secondary"
            />
          ) : null}
        </Dialog>
      )}
    </>
  );
}

/**
 * `W5-T07` §4.10, §5 row 6 — the plan and the profile's kind disagree: a switch reached Stripe and
 * stopped before the profile was written. The line says both, and **Switch** finishes that change
 * (the profile takes the plan's kind) with no Stripe call and no charge (§4.4 step 4), so it needs no
 * confirm. Moving the plan to the profile's kind instead is the ordinary switch, which asks.
 */
export function KindMismatch({
  locale,
  plan,
  profileKind,
}: {
  locale: string;
  plan: PlanKind;
  profileKind: PlanKind;
}): ReactElement {
  const { t } = useTranslation();
  const fetcher = useFetcher<BillingActionData>();
  const answer = fetcher.state === 'idle' ? fetcher.data : undefined;
  const failure = answer !== undefined && 'failure' in answer ? answer.failure : null;
  const kindName = (kind: PlanKind) => t(`billing.plan.${kind}`).toLocaleLowerCase(locale);

  return (
    <div className="mp-meta" data-testid="billing-mismatch">
      <p>{t('billing.kind_mismatch', { profile: kindName(profileKind), plan: kindName(plan) })}</p>
      {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}
      <fetcher.Form method="post" action={billingPath(locale)}>
        <input type="hidden" name="intent" value="switch" />
        <input type="hidden" name="kind" value={plan} />
        <Button type="submit" variant="secondary" isDisabled={fetcher.state !== 'idle'}>
          {t(`billing.action.switch_to.${plan}`)}
        </Button>
      </fetcher.Form>
    </div>
  );
}
